import { db, storage } from "../firebase/config";
import {
  collection,
  collectionGroup,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  orderBy,
  updateDoc,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Post, Comment } from "./connections";
import { arrayUnion, arrayRemove } from "firebase/firestore";

// import { v4 as uuidv4 } from 'uuid';

/**
 * Compress and convert an image to WebP format
 * Images are resized to fit within maxWidth x maxHeight while maintaining aspect ratio.
 * Default dimensions match the post feed display (max-w-lg = 512px, h-80 = 320px)
 * at 2x for retina screens.
 * @param file The image file to process
 * @param maxWidth Maximum width of the output image (default 1024 = 512×2 for retina)
 * @param maxHeight Maximum height of the output image (default 640 = 320×2 for retina)
 * @param quality WebP quality (0-1)
 * @returns Promise with the compressed WebP blob
 */
export const compressImageToWebP = async (
  file: File,
  maxWidth: number = 1024,
  maxHeight: number = 640,
  quality: number = 0.8,
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        // Fit within both maxWidth and maxHeight
        const widthRatio = maxWidth / width;
        const heightRatio = maxHeight / height;
        const scale = Math.min(widthRatio, heightRatio, 1); // never upscale

        width = Math.round(width * scale);
        height = Math.round(height * scale);

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Failed to compress image"));
            }
          },
          "image/webp",
          quality,
        );
      };

      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

/**
 * Upload an image to Firebase Storage
 * @param userId The ID of the user uploading the image
 * @param postId The ID of the post
 * @param imageFile The image file to upload
 * @returns Promise with the download URL
 */
export const uploadPostImage = async (
  userId: string,
  postId: string,
  imageFile: File,
): Promise<string> => {
  try {
    // Compress the image to WebP
    const compressedBlob = await compressImageToWebP(imageFile);

    // Create a storage reference
    const storageRef = ref(storage, `posts/${userId}/${postId}/image.webp`);

    // Upload the compressed image
    const snapshot = await uploadBytes(storageRef, compressedBlob, {
      contentType: "image/webp",
    });

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error("Error uploading post image:", error);
    throw error;
  }
};

/**
 * Link logs to a post and optionally mark them as public
 * @param userId The ID of the user who owns the logs
 * @param postId The ID of the post to link to
 * @param makePublic Whether to mark the logs as public
 * @param trainingLogId Optional training log ID to link
 * @param activityLogId Optional activity log ID to link
 * @param performanceLogId Optional performance log ID to link
 * @param sessionId Optional session ID to link
 */
export const linkLogsToPost = async (
  userId: string,
  postId: string,
  makePublic: boolean,
  trainingLogId?: string,
  activityLogId?: string,
  performanceLogId?: string,
  sessionId?: string,
): Promise<void> => {
  try {
    const updatePromises: Promise<void>[] = [];

    // Prepare update data based on whether to make public
    const updateData = makePublic
      ? { linkedPostID: postId, public: true }
      : { linkedPostID: postId };

    // Update training log if provided
    if (trainingLogId) {
      const trainingLogRef = doc(
        db,
        "trainingLogs",
        userId,
        "logs",
        trainingLogId,
      );
      updatePromises.push(updateDoc(trainingLogRef, updateData));
    }

    // Update activity log if provided
    if (activityLogId) {
      const activityLogRef = doc(
        db,
        "activityLogs",
        userId,
        "logs",
        activityLogId,
      );
      updatePromises.push(updateDoc(activityLogRef, updateData));
    }

    // Update performance log if provided
    if (performanceLogId) {
      const performanceLogRef = doc(
        db,
        "performanceLogs",
        userId,
        "logs",
        performanceLogId,
      );
      updatePromises.push(updateDoc(performanceLogRef, updateData));
    }

    // Update linked session if provided
    if (sessionId) {
      const sessionRef = doc(db, "sessions", userId, "sessions", sessionId);
      updatePromises.push(updateDoc(sessionRef, updateData));
    }

    // Execute all updates in parallel
    await Promise.all(updatePromises);
  } catch (error) {
    console.error("Error linking logs to post:", error);
    throw error;
  }
};

/**
 * Create a new post
 * @param userId The ID of the user creating the post
 * @param postData The post data
 * @param imageFile Optional image file to upload
 * @param makeLogsPublic Whether to mark associated logs as public
 * @param isPrivate Whether the post is private (visible only to author)
 * @param gpsDataIsPrivate Whether GPS/route data is private (hidden from others)
 * @returns Promise with the created post ID
 */
export const createPost = async (
  userId: string,
  postData: Omit<Post, "likesCount" | "commentsCount">,
  imageFile?: File,
  makeLogsPublic: boolean = false,
  isPrivate: boolean = false,
  gpsDataIsPrivate: boolean = false,
): Promise<string> => {
  try {
    // Generate a new post ID
    const postsRef = collection(db, "posts", userId, "postsList");
    const newPostRef = doc(postsRef);
    const postId = newPostRef.id;

    // Upload image if provided
    let mediaList = postData.mediaList || [];
    if (imageFile) {
      const imageUrl = await uploadPostImage(userId, postId, imageFile);
      mediaList = [imageUrl];
    }

    // Build the post object
    const post = {
      ...postData,
      mediaList,
      isPrivate, // Add privacy setting
      gpsDataIsPrivate, // Add GPS privacy setting
      likesCount: 0,
      commentsCount: 0,
    } as Post;
    // Remove undefined fields (Firestore rejects undefined)
    const cleanedPost = Object.fromEntries(
      Object.entries(post).filter(([_, v]) => v !== undefined),
    ) as Post;

    // Save the post document
    await setDoc(newPostRef, cleanedPost);

    // Optionally link logs
    if (
      postData.trainingLogID ||
      postData.activityLogID ||
      postData.performanceLogID
    ) {
      await linkLogsToPost(
        userId,
        postId,
        makeLogsPublic,
        postData.trainingLogID,
        postData.activityLogID,
        postData.performanceLogID,
        postData.sessionID,
      );
    }

    return postId;
  } catch (error) {
    console.error("Error creating post:", error);
    throw error;
  }
};

/**
 * Get a post by ID
 * @param userId The ID of the user who created the post
 * @param postId The ID of the post
 * @returns Promise with the post data
 */
export const getPost = async (
  userId: string,
  postId: string,
): Promise<Post | null> => {
  try {
    const postRef = doc(db, "posts", userId, "postsList", postId);
    const postSnap = await getDoc(postRef);

    if (!postSnap.exists()) {
      return null;
    }

    return postSnap.data() as Post;
  } catch (error) {
    console.error("Error getting post:", error);
    throw error;
  }
};

/**
 * Resolve a post globally by post ID (across all users), then return
 * hydrated post data (including likes/comments) and its owner user ID.
 */
export const getPostById = async (
  postId: string,
): Promise<{
  userId: string;
  post: Post & { id: string; likesList: string[]; CommentsList: Comment[] };
} | null> => {
  try {
    const postsGroupRef = collectionGroup(db, "postsList");
    // Firestore does not allow collectionGroup documentId() lookup by bare ID.
    // Iterate and match by doc.id for shared-link resolution.
    const snapshot = await getDocs(postsGroupRef);
    const postDoc = snapshot.docs.find((d) => d.id === postId);
    if (!postDoc) return null;

    const ownerUserId = postDoc.ref.parent.parent?.id;
    if (!ownerUserId) return null;

    const [hydratedPost] = await getUserPosts(ownerUserId, postId);
    if (!hydratedPost) return null;

    return {
      userId: ownerUserId,
      post: hydratedPost,
    };
  } catch (error) {
    console.error("Error getting post by ID:", error);
    throw error;
  }
};

/**
 * Get the first media URL from a post
 * @param userId The ID of the user who created the post
 * @param postId The ID of the post
 * @returns Promise with the first media URL or null
 */
export const getPostFirstMedia = async (
  userId: string,
  postId: string,
): Promise<string | null> => {
  try {
    const post = await getPost(userId, postId);
    return post?.mediaList?.[0] || null;
  } catch (error) {
    console.error("Error getting post first media:", error);
    return null;
  }
};

/**
 * Update a post's notes and/or privacy settings
 * @param userId The ID of the user who owns the post
 * @param postId The ID of the post to update
 * @param newNotes The updated notes content (optional)
 * @param isPrivate The updated post privacy setting (optional)
 * @param gpsDataIsPrivate The updated GPS data privacy setting (optional)
 * @returns Promise that resolves when the update is complete
 */
export const updatePost = async (
  userId: string,
  postId: string,
  newNotes?: string,
  isPrivate?: boolean,
  gpsDataIsPrivate?: boolean,
  imagePosition?: { x: number; y: number },
): Promise<void> => {
  try {
    const postRef = doc(db, "posts", userId, "postsList", postId);

    // Build update object with only provided fields
    const updateData: any = {};
    if (newNotes !== undefined) updateData.notes = newNotes;
    if (isPrivate !== undefined) updateData.isPrivate = isPrivate;
    if (gpsDataIsPrivate !== undefined)
      updateData.gpsDataIsPrivate = gpsDataIsPrivate;
    if (imagePosition !== undefined) updateData.imagePosition = imagePosition;

    await updateDoc(postRef, updateData);
  } catch (error) {
    console.error("Error updating post:", error);
    throw error;
  }
};

/**
 * Update a post's notes and image
 * @param userId The ID of the user who owns the post
 * @param postId The ID of the post to update
 * @param newNotes The updated notes content
 * @param newImageFile Optional new image file to upload
 * @returns Promise that resolves when the update is complete
 */
export const updatePostWithImage = async (
  userId: string,
  postId: string,
  newNotes: string,
  newImageFile?: File,
  imagePosition?: { x: number; y: number },
): Promise<void> => {
  try {
    const updateData: any = {
      notes: newNotes,
    };

    // Upload new image if provided
    if (newImageFile) {
      const imageUrl = await uploadPostImage(userId, postId, newImageFile);
      updateData.mediaList = [imageUrl];
    }

    // Save image position if provided
    if (imagePosition) {
      updateData.imagePosition = imagePosition;
    }

    const postRef = doc(db, "posts", userId, "postsList", postId);
    await updateDoc(postRef, updateData);
  } catch (error) {
    console.error("Error updating post with image:", error);
    throw error;
  }
};

/**
 * Delete a post
 * @param userId The ID of the user who owns the post
 * @param postId The ID of the post to delete
 * @returns Promise that resolves when the deletion is complete
 */
export const deletePost = async (
  userId: string,
  postId: string,
): Promise<void> => {
  try {
    const postRef = doc(db, "posts", userId, "postsList", postId);
    await deleteDoc(postRef);
  } catch (error) {
    console.error("Error deleting post:", error);
    throw error;
  }
};

/**
 * Get all posts for a user, ordered by timestamp (newest first)
 * @param userId The ID of the user whose posts to retrieve
 * @returns Promise with array of posts
 */

export const getUserPosts = async (
  userId: string,
  postId?: string,
): Promise<
  (Post & { id: string; likesList: string[]; CommentsList: Comment[] })[]
> => {
  try {
    const postsRef = collection(db, "posts", userId, "postsList");
    let postDocs: any[] = [];

    // --- Fetch single or all posts ---
    if (postId) {
      const postSnap = await getDoc(doc(postsRef, postId));
      if (postSnap.exists())
        postDocs = [{ id: postSnap.id, ...postSnap.data() }];
    } else {
      const q = query(postsRef, orderBy("postTimestamp", "desc"));
      const querySnapshot = await getDocs(q);
      postDocs = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    }

    // --- Attach likes & comments ---
    const postsWithExtras = await Promise.all(
      postDocs.map(async (post) => {
        const postId = post.id;

        // ----- Likes -----
        const likesCollectionRef = collection(
          db,
          "postLikes",
          userId,
          "postsList",
          postId,
          "likes",
        );
        const likesSnapshot = await getDocs(likesCollectionRef);
        const likesList = likesSnapshot.docs.map((doc) => doc.id); // Document IDs are user IDs

        // ----- Comments -----
        const commentsCollectionRef = collection(
          db,
          "postComments",
          userId,
          "postsList",
          postId,
          "comments",
        );
        const commentsSnapshot = await getDocs(commentsCollectionRef);
        const CommentsList: Comment[] = commentsSnapshot.docs
          .map((doc) => ({
            commentId: doc.id,
            userId: doc.data().userId,
            text: doc.data().text,
            timestamp: doc.data().timestamp,
          }))
          .sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis()); // newest first

        return {
          ...(post as Post),
          id: postId,
          likesList,
          CommentsList,
        };
      }),
    );

    return postsWithExtras;
  } catch (error) {
    console.error("Error getting user posts:", error);
    throw error;
  }
};

/**
 * Get user posts with pagination
 * @param userId The ID of the user whose posts to retrieve
 * @param limit Maximum number of posts to return
 * @param lastPostTimestamp Timestamp of the last post for pagination
 * @returns Promise with array of posts
 */
export const getUserPostsPaginated = async (
  userId: string,
  limit: number = 10,
  lastPostTimestamp?: Date,
): Promise<
  (Post & { id: string; likesList: string[]; CommentsList: Comment[] })[]
> => {
  try {
    const postsRef = collection(db, "posts", userId, "postsList");
    const q = query(postsRef, orderBy("postTimestamp", "desc"));
    const querySnapshot = await getDocs(q);

    let postDocs: any[] = [];
    let count = 0;

    for (const docSnap of querySnapshot.docs) {
      if (count >= limit) break;

      const data = docSnap.data();
      const postTimestamp = data.postTimestamp?.toDate?.() || new Date(0);

      // Skip posts that are newer than or equal to the lastPostTimestamp (for pagination)
      if (lastPostTimestamp && postTimestamp >= lastPostTimestamp) {
        continue;
      }

      postDocs.push({ id: docSnap.id, ...data });
      count++;
    }

    // --- Attach likes & comments ---
    const postsWithExtras = await Promise.all(
      postDocs.map(async (post) => {
        const postId = post.id;

        // ----- Likes -----
        const likesCollectionRef = collection(
          db,
          "postLikes",
          userId,
          "postsList",
          postId,
          "likes",
        );
        const likesSnapshot = await getDocs(likesCollectionRef);
        const likesList = likesSnapshot.docs.map((doc) => doc.id);

        // ----- Comments -----
        const commentsCollectionRef = collection(
          db,
          "postComments",
          userId,
          "postsList",
          postId,
          "comments",
        );
        const commentsSnapshot = await getDocs(commentsCollectionRef);
        const CommentsList: Comment[] = commentsSnapshot.docs
          .map((doc) => ({
            commentId: doc.id,
            userId: doc.data().userId,
            text: doc.data().text,
            timestamp: doc.data().timestamp,
          }))
          .sort((a, b) => b.timestamp.toMillis() - a.timestamp.toMillis());

        return {
          ...(post as Post),
          id: postId,
          likesList,
          CommentsList,
        };
      }),
    );

    return postsWithExtras;
  } catch (error) {
    console.error("Error getting user posts paginated:", error);
    throw error;
  }
};

// ==========================
// ADD LIKE
// ==========================
export const addLikeToPost = async (
  postId: string,
  currentUserId: string,
  postOwnerId: string, // NEW: the author of the post
): Promise<void> => {
  const likeRef = doc(
    db,
    "postLikes",
    postOwnerId,
    "postsList",
    postId,
    "likes",
    currentUserId,
  );

  await setDoc(likeRef, {
    timestamp: Timestamp.now(),
  });
};

export const removeLikeFromPost = async (
  postId: string,
  currentUserId: string,
  postOwnerId: string,
): Promise<void> => {
  const likeRef = doc(
    db,
    "postLikes",
    postOwnerId,
    "postsList",
    postId,
    "likes",
    currentUserId,
  );
  await deleteDoc(likeRef);
};

// ==========================
// ADD COMMENT
// ==========================

export const addCommentToPost = async (
  postId: string,
  currentUserId: string,
  text: string,
  postOwnerId: string, // Post owner ID
): Promise<string> => {
  try {
    const commentId = crypto.randomUUID();
    const commentRef = doc(
      db,
      "postComments",
      postOwnerId,
      "postsList",
      postId,
      "comments",
      commentId,
    );

    await setDoc(commentRef, {
      userId: currentUserId,
      text,
      timestamp: Timestamp.now(),
    });

    return commentId;
  } catch (error) {
    console.error("Error adding comment:", error);
    throw error;
  }
};

// ==========================
// EDIT COMMENT
// ==========================
export const editCommentOnPost = async (
  postId: string,
  postOwnerId: string,
  commentId: string,
  newText: string,
): Promise<void> => {
  try {
    const commentRef = doc(
      db,
      "postComments",
      postOwnerId,
      "postsList",
      postId,
      "comments",
      commentId,
    );

    await updateDoc(commentRef, {
      text: newText,
      timestamp: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error editing comment:", error);
    throw error;
  }
};

// ==========================
// DELETE COMMENT
// ==========================
export const deleteCommentFromPost = async (
  postOwnerId: string,
  postId: string,
  commentId: string,
): Promise<void> => {
  try {
    const commentRef = doc(
      db,
      "postComments",
      postOwnerId,
      "postsList",
      postId,
      "comments",
      commentId,
    );
    await deleteDoc(commentRef);
  } catch (error) {
    console.error("Error deleting comment:", error);
    throw error;
  }
};
